import React from 'react';
import { Chess } from 'chess.js';
import { PgnNode, serializePgnTree, deserializePgnTree } from '../../utils';
import useSound from 'use-sound';
import moveSound from '../../sounds/Move.mp3';
import captureSound from '../../sounds/Capture.mp3';
import kachingSound from '../../sounds/Kaching.m4a';
import wrongSound from '../../sounds/Wrong.m4a';

export const DataContext = React.createContext();

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// When a user drags the king to the rook's square (e.g. e1→h1), chess.js
// rejects it because it only recognises king-moves-two-squares (e1→g1).
// Remap corner-drop targets so castling works either way.
const CASTLING_REMAPS = {
  e1: { h1: 'g1', a1: 'c1' },
  e8: { h8: 'g8', a8: 'c8' },
};

function normalizeCastlingMove(chess, moveInput) {
  if (typeof moveInput !== 'object') return moveInput;
  const piece = chess.get(moveInput.from);
  if (!piece || piece.type !== 'k') return moveInput;
  const newTo = CASTLING_REMAPS[moveInput.from]?.[moveInput.to];
  return newTo ? { ...moveInput, to: newTo } : moveInput;
}

function DataProvider({ children }) {
  const [fen, setFen] = React.useState(START_FEN);

  const [pgnTreeRoot, setPgnTreeRoot] = React.useState(null);
  const [currentNode, setCurrentNode] = React.useState(null);

  const [repertoires, setRepertoires] = React.useState([]);
  const [currentRepertoireId, setCurrentRepertoireId] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [currentMode, setCurrentMode] = React.useState('builder');
  const [trainingState, setTrainingState] = React.useState({
    isActive: false,
    currentNode: null,
    nextMove: null,
    progress: 0,
    totalNodes: 0,
    visitedNodes: new Set(),
    userColor: 'white',
  });
  const [boardOrientation, setBoardOrientation] = React.useState('white');
  const toggleBoardOrientation = () => {
    const next = boardOrientation === 'white' ? 'black' : 'white';
    setBoardOrientation(next);
    if (trainingState.isActive) {
      startTraining(next);
    }
  };

  const [muted, setMuted] = React.useState(false);
  const [volume, setVolume] = React.useState(0.4);

  const [highlightedSquares, setHighlightedSquares] = React.useState({});
  const [customArrows, setCustomArrows] = React.useState([]);
  const hintLevelRef = React.useRef(0);
  const hintTimeoutRef = React.useRef(null);

  const [playMove] = useSound(moveSound, { volume, soundEnabled: !muted });
  const [playCapture] = useSound(captureSound, {
    volume,
    soundEnabled: !muted,
  });
  const [playKaching] = useSound(kachingSound, {
    volume,
    soundEnabled: !muted,
  });
  const [playWrong] = useSound(wrongSound, { volume, soundEnabled: !muted });

  const setMode = (mode) => {
    setCurrentMode(mode);
  };

  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL.replace(/\/$/, '')}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(
          `API request failed: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      throw error;
    }
  };

  const loadRepertoiresFromAPI = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const pgns = await apiRequest('/pgns');

      const repertoires = pgns.map((pgn, index) => {
        let tree;
        if (pgn.pgn) {
          try {
            const pgnData = JSON.parse(pgn.pgn);
            tree = deserializePgnTree(pgnData, START_FEN);
          } catch (parseError) {
            tree = new PgnNode(null);
          }
        } else {
          tree = new PgnNode(null);
        }

        return {
          id: pgn.id,
          name: pgn.openingName,
          tree: tree,
          notes: pgn.notes || '',
          attempts: pgn.attempts || 0,
          successPercentage: pgn.successPercentage || 0,
          accuracy: pgn.accuracy || 0,
          failedRecentFens: pgn.failedRecentFens || [],
        };
      });

      setRepertoires(repertoires);

      if (repertoires.length > 0) {
        loadRepertoire(repertoires[0].id, repertoires);
      } else {
        initializeDefault();
      }
    } catch (error) {
      setError(error.message);
      initializeDefault();
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (API_BASE_URL && API_KEY) {
      loadRepertoiresFromAPI();
    } else {
      initializeDefault();
    }
  }, []);

  const initializeDefault = () => {
    const rootNode = new PgnNode(null);
    rootNode.fen = START_FEN;

    const defaultRepertoire = {
      id: crypto.randomUUID(),
      name: 'My First Opening (e4)',
      tree: rootNode,
      notes: 'Start building your opening repertoire here!',
      attempts: 0,
      successPercentage: 0,
      accuracy: 0,
      failedRecentFens: [],
    };
    const initialData = [defaultRepertoire];
    setRepertoires(initialData);
    loadRepertoire(defaultRepertoire.id, initialData);

    setFen(START_FEN);
  };

  const saveRepertoireToAPI = async (repertoire) => {
    try {
      const pgnText = serializePgnTree(repertoire.tree);
      const apiData = {
        openingName: repertoire.name,
        pgn: pgnText,
        notes: repertoire.notes || '',
        attempts: repertoire.attempts || 0,
        successPercentage: repertoire.successPercentage || 0,
        accuracy: repertoire.accuracy || 0,
        failedRecentFens: repertoire.failedRecentFens || [],
      };

      if (repertoire.id && !repertoire.id.startsWith('temp-')) {
        await apiRequest(`/pgn`, {
          method: 'PUT',
          body: JSON.stringify({ id: repertoire.id, ...apiData }),
        });
      } else {
        const response = await apiRequest('/pgn', {
          method: 'POST',
          body: JSON.stringify(apiData),
        });
        repertoire.id = response.pgn_id;
      }
    } catch (error) {
      throw error;
    }
  };

  const loadRepertoire = (id, currentRepertoires = repertoires) => {
    const repertoire = currentRepertoires.find((r) => r.id === id);

    if (repertoire) {
      let tree = repertoire.tree;
      if (!tree || !tree.fen) {
        tree = new PgnNode(null);
        tree.fen = START_FEN;
        const updatedRepertoires = currentRepertoires.map((r) => {
          if (r.id === id) {
            return { ...r, tree: tree };
          }
          return r;
        });
        setRepertoires(updatedRepertoires);
      }

      setPgnTreeRoot(tree);
      setCurrentRepertoireId(id);
      updateGameState(tree);
    }
  };

  const createRepertoire = async (name) => {
    const rootNode = new PgnNode(null);
    rootNode.fen = START_FEN;

    const newRepertoire = {
      id: `temp-${crypto.randomUUID()}`,
      name: name,
      tree: rootNode,
      notes: '',
      attempts: 0,
      successPercentage: 0,
      accuracy: 0,
      failedRecentFens: [],
    };

    const updatedRepertoires = [...repertoires, newRepertoire];
    setRepertoires(updatedRepertoires);

    loadRepertoire(newRepertoire.id, updatedRepertoires);

    if (!fen || fen !== START_FEN) {
      setFen(START_FEN);
    }

    if (API_BASE_URL && API_KEY) {
      try {
        await saveRepertoireToAPI(newRepertoire);
        await loadRepertoiresFromAPI();
      } catch (error) {
      }
    }
  };

  const deleteRepertoire = async (id) => {
    try {
      if (!id.startsWith('temp-')) {
        await apiRequest(`/pgn?id=${id}`, { method: 'DELETE' });
      }

      const updatedRepertoires = repertoires.filter((r) => r.id !== id);
      setRepertoires(updatedRepertoires);

      if (currentRepertoireId === id) {
        if (updatedRepertoires.length > 0) {
          loadRepertoire(updatedRepertoires[0].id, updatedRepertoires);
        } else {
          initializeDefault();
        }
      }
    } catch (error) {
    }
  };

  const updateGameState = (node) => {
    setCurrentNode(node);
    const validFen = node && node.fen ? node.fen : START_FEN;
    setFen(validFen);
  };

  const makeMove = (moveInput) => {
    if (!currentRepertoireId || !currentNode) {
      return null;
    }

    if (!fen) {
      return null;
    }

    const chessCopy = new Chess(fen);
    moveInput = normalizeCastlingMove(chessCopy, moveInput);

    const moveNumber = chessCopy.moveNumber();

    let move;
    try {
      if (typeof moveInput === 'object' && !moveInput.promotion) {
        moveInput.promotion = 'q';
      }
      move = chessCopy.move(moveInput);
    } catch (e) {
      return null;
    }

    if (move) {
      const { node: nextNode, isNew } = currentNode.addMove(move, moveNumber);

      if (move.flags.includes('c') || move.flags.includes('e')) {
        playCapture();
      } else {
        playMove();
      }

      if (isNew) {
        const updatedRepertoires = repertoires.map((r) => {
          if (r.id === currentRepertoireId) {
            return { ...r, tree: pgnTreeRoot };
          }
          return r;
        });
        setRepertoires(updatedRepertoires);

        if (API_BASE_URL && API_KEY) {
          const currentRepertoire = updatedRepertoires.find(
            (r) => r.id === currentRepertoireId
          );
          if (currentRepertoire) {
            saveRepertoireToAPI(currentRepertoire).catch((error) => {
            });
          }
        }
      }

      updateGameState(nextNode);
      return move;
    }
    return null;
  };

  const navigateBack = () => {
    if (currentNode && currentNode.parent) {
      if (currentNode.move) {
        if (
          currentNode.move.flags.includes('c') ||
          currentNode.move.flags.includes('e')
        ) {
          playCapture();
        } else {
          playMove();
        }
      }
      updateGameState(currentNode.parent);
    }
  };

  const navigateForward = (variationIndex = 0) => {
    if (currentNode && currentNode.children.length > 0) {
      if (variationIndex >= 0 && variationIndex < currentNode.children.length) {
        const nextNode = currentNode.children[variationIndex];
        if (nextNode.move) {
          if (
            nextNode.move.flags.includes('c') ||
            nextNode.move.flags.includes('e')
          ) {
            playCapture();
          } else {
            playMove();
          }
        }
        updateGameState(nextNode);
      }
    }
  };

  const navigateToNode = (node) => {
    if (node) {
      if (node.move) {
        if (node.move.flags.includes('c') || node.move.flags.includes('e')) {
          playCapture();
        } else {
          playMove();
        }
      }
      updateGameState(node);
    }
  };

  const findNextUnvisitedNode = (startNode, visitedNodes) => {
    const stack = [...startNode.children].reverse();
    while (stack.length > 0) {
      const node = stack.pop();
      if (!visitedNodes.has(node.fen)) {
        return node;
      }
      const children = [...node.children].reverse();
      for (const child of children) {
        stack.push(child);
      }
    }
    return null;
  };

  const handleLineEnd = (lastNode, visitedNodes) => {
    let ancestor = lastNode;

    while (ancestor) {
      const nextTargetNode = findNextUnvisitedNode(ancestor, visitedNodes);
      if (nextTargetNode) {
        alert('Line complete! Moving to the next variation.');
        updateGameState(ancestor);

        setTimeout(() => {
          if (!trainingState.isActive) return;

          const chessTurn = ancestor.fen.includes(' w ') ? 'white' : 'black';
          const trainerPlaysAs =
            trainingState.userColor === 'white' ? 'black' : 'white';

          if (chessTurn === trainerPlaysAs) {
            playTrainerMove(ancestor, visitedNodes);
          } else {
            findNextTrainingMove(ancestor, visitedNodes);
          }
        }, 1000);
        return;
      }
      ancestor = ancestor.parent;
    }

    alert('Congratulations! You have completed training for this repertoire!');
    playKaching();
    stopTraining();
  };

  const findNextTrainingMove = (startNode, currentVisitedNodes) => {
    if (!startNode) return;

    const nextUnvisitedNode = findNextUnvisitedNode(
      startNode,
      currentVisitedNodes
    );

    if (nextUnvisitedNode) {
      setTrainingState((prev) => ({
        ...prev,
        nextMove: nextUnvisitedNode.move,
        currentNode: nextUnvisitedNode.parent,
      }));
    } else {
      handleLineEnd(startNode, currentVisitedNodes);
    }
  };

  const playTrainerMove = (currentTrainerNode, currentVisitedNodes) => {
    if (!trainingState.isActive || !currentTrainerNode) return;

    const targetNode = findNextUnvisitedNode(
      currentTrainerNode,
      currentVisitedNodes
    );

    if (targetNode) {
      let nextStepNode = targetNode;
      while (
        nextStepNode.parent &&
        nextStepNode.parent !== currentTrainerNode
      ) {
        nextStepNode = nextStepNode.parent;
      }

      const chessCopy = new Chess(currentTrainerNode.fen);
      try {
        const move = chessCopy.move({
          from: nextStepNode.move.from,
          to: nextStepNode.move.to,
          promotion: nextStepNode.move.promotion || 'q',
        });

        if (move) {
          if (move.flags.includes('c') || move.flags.includes('e')) {
            playCapture();
          } else {
            playMove();
          }

          const newVisitedNodes = new Set([
            ...currentVisitedNodes,
            nextStepNode.fen,
          ]);
          const newProgress = Math.round(
            (newVisitedNodes.size / trainingState.totalNodes) * 100
          );

          setTrainingState((prev) => ({
            ...prev,
            visitedNodes: newVisitedNodes,
            progress: newProgress,
          }));

          updateGameState(nextStepNode);
          findNextTrainingMove(nextStepNode, newVisitedNodes);
        }
      } catch (e) {
        /* silent fail */
      }
    } else {
      handleLineEnd(currentTrainerNode, currentVisitedNodes);
    }
  };

  const makeTrainingMove = (moveInput) => {
    if (!trainingState.isActive || !trainingState.nextMove) return null;
    if (!fen) return null;

    const expectedMove = trainingState.nextMove;
    const chessCopy = new Chess(fen);
    moveInput = normalizeCastlingMove(chessCopy, moveInput);
    let move;
    try {
      if (typeof moveInput === 'object' && !moveInput.promotion) {
        moveInput.promotion = 'q';
      }
      move = chessCopy.move(moveInput);
    } catch (e) {
      return null;
    }

    if (
      move &&
      move.from === expectedMove.from &&
      move.to === expectedMove.to &&
      (move.promotion || 'q') === (expectedMove.promotion || 'q')
    ) {
      if (move.flags.includes('c') || move.flags.includes('e')) {
        playCapture();
      } else {
        playMove();
      }
      playKaching();

      const nextNode = trainingState.currentNode.children.find(
        (child) => child.move.san === move.san
      );

      if (nextNode) {
        const newVisitedNodes = new Set([
          ...trainingState.visitedNodes,
          nextNode.fen,
        ]);
        const newProgress = Math.round(
          (newVisitedNodes.size / trainingState.totalNodes) * 100
        );

        setTrainingState((prev) => ({
          ...prev,
          visitedNodes: newVisitedNodes,
          progress: newProgress,
        }));

        updateGameState(nextNode);

        clearHint();

        setTimeout(() => {
          playTrainerMove(nextNode, newVisitedNodes);
        }, 500);

        return move;
      }
    } else {
      playWrong();
    }
    return null;
  };

  const clearHint = () => {
    setHighlightedSquares({});
    setCustomArrows([]);
    hintLevelRef.current = 0;
    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
      hintTimeoutRef.current = null;
    }
  };

  const showHint = () => {
    if (!trainingState.isActive || !trainingState.nextMove) return;

    const { from, to } = trainingState.nextMove;

    if (hintTimeoutRef.current) {
      clearTimeout(hintTimeoutRef.current);
    }

    if (hintLevelRef.current === 0) {
      setHighlightedSquares({
        [from]: {
          backgroundColor: 'rgba(46, 182, 174, 0.55)',
          borderRadius: '50%',
          boxShadow: 'inset 0 0 0 4px rgba(20, 160, 150, 0.85)',
        },
      });
      hintLevelRef.current = 1;
    } else {
      setCustomArrows([[from, to, 'rgba(20, 160, 150, 0.8)']]);
      setHighlightedSquares({
        [from]: { backgroundColor: 'rgba(46, 182, 174, 0.45)' },
        [to]: { backgroundColor: 'rgba(46, 182, 174, 0.45)' },
      });
      hintLevelRef.current = 2;
      hintTimeoutRef.current = setTimeout(() => {
        clearHint();
      }, 5000);
    }
  };

  const startTraining = (orientation = boardOrientation) => {
    if (!pgnTreeRoot || pgnTreeRoot.children.length === 0) {
      alert('Cannot start training on an empty repertoire.');
      return;
    }

    setCurrentMode('trainer');
    updateGameState(pgnTreeRoot);

    const userPlaysAs = orientation === 'white' ? 'white' : 'black';
    const totalNodes = countNodes(pgnTreeRoot);

    const initialState = {
      isActive: true,
      currentNode: pgnTreeRoot,
      nextMove: null,
      progress: 0,
      totalNodes,
      visitedNodes: new Set([pgnTreeRoot.fen]),
      userColor: userPlaysAs,
    };
    setTrainingState(initialState);
    clearHint();
  };

  const stopTraining = () => {
    setTrainingState({
      isActive: false,
      currentNode: null,
      nextMove: null,
      progress: 0,
      totalNodes: 0,
      visitedNodes: new Set(),
      userColor: 'white',
    });
    setCurrentMode('builder');
    updateGameState(pgnTreeRoot);
    clearHint();
  };

  const restartTrainingWithOrientation = (orientation) => {
    if (trainingState.isActive) {
      setBoardOrientation(orientation);
      startTraining(orientation);
    }
  };

  const countNodes = (node) => {
    if (!node) return 0;
    let count = 1;
    for (const child of node.children) {
      count += countNodes(child);
    }
    return count;
  };

  const getCurrentPgn = () => {
    if (!currentNode) return '';

    const chess = new Chess();
    const moves = [];
    let node = currentNode;

    while (node.parent) {
      if (node.move) {
        moves.unshift(node.move);
      }
      node = node.parent;
    }

    moves.forEach((move) => {
      chess.move(move);
    });

    return chess.pgn();
  };

  const updateNodeComment = async (node, comment) => {
    if (!node || !currentRepertoireId) return;

    node.comment = comment;

    const updatedRepertoires = repertoires.map((r) => {
      if (r.id === currentRepertoireId) {
        return { ...r, tree: pgnTreeRoot };
      }
      return r;
    });
    setRepertoires(updatedRepertoires);

    if (API_BASE_URL && API_KEY) {
      const currentRepertoire = updatedRepertoires.find(
        (r) => r.id === currentRepertoireId
      );
      if (currentRepertoire) {
        try {
          await saveRepertoireToAPI(currentRepertoire);
        } catch (error) {
        }
      }
    }
  };

  const deleteMove = async (nodeToDelete) => {
    if (!nodeToDelete || !currentRepertoireId) return false;

    if (!nodeToDelete.parent) {
      return false;
    }

    const isCurrentNodeOrDescendant = (() => {
      let current = currentNode;
      while (current) {
        if (current === nodeToDelete) return true;
        current = current.parent;
      }
      return false;
    })();

    const deleted = nodeToDelete.deleteNode();
    if (!deleted) return false;

    if (isCurrentNodeOrDescendant) {
      updateGameState(nodeToDelete.parent);
    }

    const updatedRepertoires = repertoires.map((r) => {
      if (r.id === currentRepertoireId) {
        return { ...r, tree: pgnTreeRoot };
      }
      return r;
    });
    setRepertoires(updatedRepertoires);

    if (API_BASE_URL && API_KEY) {
      const currentRepertoire = updatedRepertoires.find(
        (r) => r.id === currentRepertoireId
      );
      if (currentRepertoire) {
        try {
          await saveRepertoireToAPI(currentRepertoire);
        } catch (error) {
          console.error('Failed to save deleted move to API:', error);
        }
      }
    }

    return true;
  };

  React.useEffect(() => {
    if (trainingState.isActive && trainingState.currentNode === pgnTreeRoot) {
      const chessTurn = fen.includes(' w ') ? 'white' : 'black';
      const trainerPlaysAs =
        trainingState.userColor === 'white' ? 'black' : 'white';

      if (chessTurn === 'white' && trainerPlaysAs === 'white') {
        setTimeout(() => {
          if (trainingState.isActive) {
            playTrainerMove(pgnTreeRoot, trainingState.visitedNodes);
          }
        }, 1000);
      } else {
        findNextTrainingMove(pgnTreeRoot, trainingState.visitedNodes);
      }
    }
  }, [trainingState.isActive, trainingState.currentNode, pgnTreeRoot]);

  React.useEffect(() => {
    if (!fen) {
      setFen(START_FEN);
    }
  }, [fen]);

  if (isLoading) return <div>Loading Repertoires...</div>;
  if (error) return <div>Error loading repertoires: {error}</div>;
  if (!pgnTreeRoot) return <div>Initializing...</div>;
  if (!fen) return <div>Initializing game state...</div>;

  return (
    <DataContext.Provider
      value={{
        fen,
        currentNode,
        makeMove,
        navigateBack,
        navigateForward,
        navigateToNode,
        pgnTreeRoot,
        getCurrentPgn,
        updateNodeComment,
        deleteMove,

        repertoires,
        currentRepertoireId,
        loadRepertoire,
        createRepertoire,
        deleteRepertoire,

        isLoading,
        error,

        currentMode,
        setMode,
        trainingState,
        startTraining,
        stopTraining,
        restartTrainingWithOrientation,
        makeTrainingMove,
        playTrainerMove,
        boardOrientation,
        setBoardOrientation,
        toggleBoardOrientation,

        muted,
        setMuted,
        volume,
        setVolume,
        playMove,
        playCapture,
        playKaching,
        playWrong,

        highlightedSquares,
        customArrows,
        showHint,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export default DataProvider;
